import { useEffect, useState } from "react";
import { FaLink } from "react-icons/fa6";
import { FaRegFilePdf } from "react-icons/fa";
import { IoCallOutline } from "react-icons/io5";
import { IoImage } from "react-icons/io5";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "react-phone-number-input/style.css";
import toast from "react-hot-toast";
import PhoneInputWithCountry from "react-phone-number-input/react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import Header from "./header";
import { getPrivateQRCodes, updateUserQRCodes } from "../api/qrcode";
import { Link, useParams } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { TbGrave2 } from "react-icons/tb";
import Memories from "./componets/Memories";
import ImageVideos from "./componets/ImageVideos";
import Pdf from "./componets/Pdf";
import { uploadToS3 } from "../utils/uploadToS3";
import SmartOR from "./componets/SmartOR";

const schema = (activeTab) => {
  const urlValidation = yup
    .string()
    .matches(
      /^(?:(?:https?:\/\/)?[^\s"']+\.([^.\s"']{2,}|.{2,5})|[^.\s"']{2,}\.[^.\s"']{2,})?$/,
      "Link must start with https:// or www."
    )
    .nullable();
  switch (activeTab) {
    case 1:
      return yup.object({
        link: yup
          .string()
          .matches(
            /^(?:(?:https?:\/\/)?[^\s"']+\.([^.\s"']{2,}|.{2,5})|[^.\s"']{2,}\.[^.\s"']{2,})?$/,
            "Invalid URL format"
          )
          .nullable(),
        name: yup.string("Name must be string"),
      });
    case 2:
      return yup.object({
        contactNum: yup
          .string()
          .test("is-valid", "Invalid phone number", (value) => {
            if (!value) return true;
            return isValidPhoneNumber(value);
          })
          .nullable(),
        name: yup.string("Name must be string"),
      });
    case 3:
      return yup.object().shape({
        title: yup.string(),
        subTitle: yup.string().optional().nullable(),
        topImage: yup.mixed().optional().nullable(),
        freeText: yup.string(),
        videoUrl: yup
          .string()
          .matches(
            /^(?:(?:https?:\/\/)?[^\s"']+\.([^.\s"']{2,}|.{2,5})|[^.\s"']{2,}\.[^.\s"']{2,})?$/,
            "Invalid URL format"
          )
          .nullable(),
        description: yup.string().max(4000, "Description is too long"),
        imageText: yup
          .string()
          .max(4000, "Image gallery description is too long"),
        pdfTitle: yup.string(),
        isProtected: yup.boolean(),
        password: yup.string().when("isProtected", {
          is: true,
          then: () =>
            yup
              .string()
              .min(6, "Password must be at least 6 characters long")
              .max(16, "Password must be at most 16 characters long")
              .required("Password is required"),
          otherwise: () => yup.string().nullable(),
        }),
      });
    case 4:
      return yup.object({
        name: yup.string(),
      });
    case 5:
      return yup.object({
        name: yup.string(),
      });
    case 6:
      return yup.object({
        defaultUrl: urlValidation.label("Default URL"),
      });

    default:
      return yup.object();
  }
};

const EditQr = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  // tab
  const changeTab = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  // form
  const methods = useForm({
    resolver: yupResolver(schema(activeTab)),
    defaultValues: {
      link: null,
      name: "",
    },
    mode: "onChange",
  });
  
  const {
    handleSubmit,
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const onSubmit = async (data) => {
    
    
    setProcessing(true);
    let obj = {
      name: data.name,
      type: tabMapper[activeTab],
    };

    try {
      if (tabMapper[activeTab] === "memories") {
        obj["memories"] = {
          title: data.title,
          subTitle: data.subTitle,
          freeText: data.freeText,
          videoUrl: data.videoUrl,
          videoDescription: data.videoDescription,
          imageText: data.imageText,
          pdfTitle: data.pdfTitle,
          isProtected: data.isProtected,
          password: data.isProtected ? data.password : null,
        };

        const uploadPromises = [];
        // Top Image
       if (data.topImage) {
         // Handle both old string format and new object format
         let blobUrl, originalFileName;
         
         if (typeof data.topImage === 'string') {
           // Old format - just the blob URL
           blobUrl = data.topImage;
           originalFileName = data.topImage.split('/').pop() || "topImage.jpg"; // extract filename from URL or use fallback
         } else if (typeof data.topImage === 'object' && data.topImage?.url) {
           // New format - object with url and originalName
           blobUrl = data.topImage.url;
           originalFileName = data.topImage.originalName || "topImage.jpg";
         } else {
           // Fallback for any other case
           blobUrl = data.topImage;
           originalFileName = "topImage.jpg";
         }

         // Check if this is already an S3 URL (already uploaded)
         const isS3Url = blobUrl.includes('s3.amazonaws.com') || blobUrl.includes('amazonaws.com');
         
         if (isS3Url) {
           // Already uploaded to S3, just use the existing URL
           obj.memories["topImage"] = blobUrl;
         } else {
           // This is a local blob URL, need to upload it
           try {
             const blob = await fetch(blobUrl).then((res) => res.blob());
             console.log("blob", blob);
             
             const file = new File([blob], originalFileName, { type: blob.type });

             // Upload single file to S3
             const result = await uploadToS3([file], "memories", data?._id);

             // Extract uploaded file URL
             const publicUrl = result.publicUrls[0];

             // Store it
             obj.memories["topImage"] = publicUrl;
           } catch (fetchError) {
             console.error("Error fetching blob for topImage:", fetchError);
             // If fetch fails, assume it's already an S3 URL and use it directly
             obj.memories["topImage"] = blobUrl;
           }
         }
        
       } else {
         obj.memories["topImage"] = null;
       }

        // Videos
        if (data.videos?.length) {
          const newVideos = [];
          const preVideos = [];
          data.videos.forEach((item, idx) => {
            if (item instanceof File) {
              newVideos.push({
                index: Number(item?.index) || idx,
                url: item,
              });
            } else if (item.url instanceof File) {
              newVideos.push({
                index: Number(item?.index) || idx,
                url: item.url,
              });
            } else if (typeof item?.url === "string") {
              preVideos.push({
                index: Number(item?.index),
                url: item?.url,
              });
            } else if (typeof item?.url?.url === "string") {
              preVideos.push({
                index: Number(item?.index),
                url: item?.url?.url,
              });
            }
          });

          if (newVideos.length > 0) {
            uploadPromises.push(
              uploadToS3(
                newVideos.map((ele) => ele.url), // Pass only File objects
                "memories",
                data?._id
              ).then((result) => {
                const currentData = newVideos.map((ele, idx) => {
                  const publicUrl = result.publicUrls[idx];
                  return {
                    index: ele.index,
                    url: publicUrl,
                  };
                });

                return {
                  type: "videos",
                  currentData,
                };
              })
            );
          }

          obj["memories"]["videos"] = preVideos;
        } else {
          obj["memories"]["videos"] = [];
        }

        // Images
        if (data.images?.length) {
          const newImages = [];
          const preImages = [];

          data.images.forEach((item, idx) => {
            if (item instanceof File) {
              newImages.push({
                index: Number(item?.index) || idx,
                url: item,
              });
            } else if (item.url instanceof File) {
              newImages.push({
                index: Number(item?.index) || idx,
                url: item.url,
              });
            } else if (typeof item?.url === "string") {
              preImages.push({
                index: Number(item?.index),
                url: item?.url,
              });
            } else if (typeof item?.url?.url === "string") {
              preImages.push({
                index: Number(item?.index),
                url: item?.url?.url,
              });
            }
          });

          if (newImages.length > 0) {
            uploadPromises.push(
              uploadToS3(
                newImages.map((ele) => ele.url),
                "memories",
                data?._id
              ).then((result) => {
                const currentData = newImages.map((url, idx) => {
                  const publicUrl = result.publicUrls[idx];
                  return {
                    index: url.index,
                    url: publicUrl,
                  };
                });
                return {
                  type: "images",
                  currentData,
                };
              })
            );
          }

          // Store preImages now; uploaded ones will be added later
          obj.memories["images"] = preImages;
        } else {
          obj.memories["images"] = [];
        }

        // PDFs
        if (data.pdfs?.length) {
          const newPdfs = data.pdfs.filter((ele) => ele instanceof File);
          const prePdfs = data.pdfs.filter((ele) => typeof ele === "string");
          if (newPdfs.length > 0) {
            uploadPromises.push(
              uploadToS3(newPdfs, "memories", data?._id).then((result) => {
                return {
                  type: "pdfs",
                  currentData: result.publicUrls,
                };
              })
            );
          }
          obj["memories"]["pdfs"] = prePdfs;
        } else {
          obj["memories"]["pdfs"] = [];
        }

        const uploadResults = await Promise.all(uploadPromises);

        uploadResults.forEach(({ type, currentData }) => {
          if (!obj.memories[type]) {
            obj.memories[type] = [];
          }

          let formattedArray;
          if (type === "videos" || type === "images" ) {
            formattedArray = Array.isArray(currentData)
              ? currentData.map((item) => ({
                  index: item.index,
                  url: item.url,
                }))
              : [];
          }
          if (type === "videos" || type === "images") {
            obj.memories[type] = [...obj.memories[type], ...formattedArray];
          } else {
            obj["memories"][type] = [...obj["memories"][type], ...currentData];
          }
        });
      } else if (tabMapper[activeTab] === "link") {
        obj["link"] = data.link.toLowerCase();
      } else if (tabMapper[activeTab] === "phoneNumber") {
        obj["phoneNumber"] = data.contactNum;
      } else if (tabMapper[activeTab] === "image") {
        const uploadPromises = [];

        if (data.imageVideos?.length) {
          const newImages = data.imageVideos.filter(
            (ele) => ele instanceof File
          );
          const preImages = data.imageVideos.filter(
            (ele) => typeof ele === "string"
          );
          if (newImages.length > 0) {
            uploadPromises.push(uploadToS3(newImages, "image", data?._id));
          }
          obj["imageVideos"] = preImages;

          const uploadResults = await Promise.all(uploadPromises);

          uploadResults.forEach((result) => {
            obj["imageVideos"] = [...obj["imageVideos"], ...result.publicUrls];
          });
        } else {
          obj["imageVideos"] = [];
        }
      } else if (tabMapper[activeTab] === "pdf") {
        obj["link"] = barcodeData?.storedInfo?.pdf;
      } else if (tabMapper[activeTab] === "smartQR") {
        // Get smartQR data from form
        const { defaultUrl } = watch();
        const smartQR = data.smartQR || barcodeData?.storedInfo?.smartQR || {};

        // Ensure we have the correct structure
        const updatedSmartQR = {
          conditions: Array.isArray(smartQR.conditions)
            ? smartQR.conditions.map((condition) => ({
                parameter: condition.parameter || "time",
                from: condition.from || "",
                to: condition.to || "",
                url: condition.url?.toLowerCase() || "",
                timeZone: condition.timeZone || "",
              }))
            : [],
          defaultUrl: defaultUrl?.toLowerCase() || smartQR.defaultUrl?.toLowerCase() || "",
        };

        obj["smartQR"] = updatedSmartQR;
      }

      // Now that all uploads are complete, update the QR code
      await updateUserQRCodes(id, obj);
      getBarcodeData();
      toast.success("QR code updated successfully");
    } catch (error) {
      console.log("Error while updating qr code ", error);
      toast.error(
        error?.response?.data?.message || "Error while updating qr code"
      );
    } finally {
      setProcessing(false);
      window.location.reload();
    }
  };

  const tabMapper = {
    1: "link",
    2: "phoneNumber",
    3: "memories",
    4: "pdf",
    5: "image",
    6: "smartQR",
  };

  const getBarcodeData = async () => {
    try {
      const { data } = await getPrivateQRCodes(id);
      setBarcodeData(data.barcode);
      setValue(
        "contactNum",
        data?.barcode?.storedInfo?.phoneNumber
          ? data?.barcode?.storedInfo?.phoneNumber || ""
          : ""
      );
      setValue(
        "link",
        data?.barcode?.storedInfo?.link ? data?.barcode?.storedInfo?.link : ""
      );
      setValue("name", data?.barcode?.name ? data.barcode.name : "");
      const smartDefaultUrl = data?.barcode?.storedInfo?.smartQR?.defaultUrl;

      setValue("defaultUrl", smartDefaultUrl ? smartDefaultUrl : "");

      Object.keys(tabMapper).forEach((key) => {
        if (tabMapper[key] === data?.barcode?.storedInfo?.infoType) {
          changeTab(parseInt(key));
        }
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error while getting barcode data"
      );
    }
  };
  useEffect(() => {
    if (id) {
      getBarcodeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const disableButtonCondition = () => {
    let condition = false;
    if (activeTab === 3) {
      if (
        !watch("title") &&
        !watch("subTitle") &&
        !watch("freeText") &&
        !watch("videoUrl") &&
        !watch("imageText") &&
        !watch("videoDescription") &&
        !watch("pdfTitle") &&
        !watch("topImage") &&
        ((watch("images") && watch("images").length === 0) ||
          !watch("images")) &&
        ((watch("pdfs") && watch("pdfs").length === 0) || !watch("pdfs")) &&
        ((watch("videos") && watch("videos").length === 0) ||
          !watch("videos")) &&
        !watch("smartQR")
      ) {
        condition = true;
      }
    }
    return condition;
  };

  const validTypes = [
    "link",
    "phoneNumber",
    "memories",
    "image",
    "pdf",
    "smartQR",
  ];
  const infoType = barcodeData?.storedInfo?.infoType;
  const link = barcodeData?.link;
  const shouldShowLink = validTypes.includes(infoType) && !!link;

  return (
    <>
      <Header />
      <div className="max-w-7xl mx-auto">
        <div className="my-8 mx-0 lg:my-16 lg:mx-16">
          <Link
            to="/"
            className="flex items-center border border-black p-2 rounded-md w-fit"
          >
            <IoMdArrowRoundBack className="me-2" />
            Back
          </Link>
          <h2 className="text-3xl font-medium mb-3 text-center">
            Edit QR Code:
          </h2>
          <p className="text-center text-xs sm:text-base font-normal mx-3">
            Select an option, edit the content, and
            <span className="font-semibold"> press “Save”</span> to apply it to
            your QR code.
          </p>
          <div className="flex flex-col px-[20px]">
            <div className="flex justify-center my-2 flex-wrap">
              <button
                className={`${
                  activeTab === 1
                    ? "bg-tealBlue text-white"
                    : "bg-lightBlue text-white-700"
                } py-2 px-4 rounded mx-2 sm:mx-4 my-1 border flex-col justify-center hover:bg-tealBlue  border-borderMuted  flex  focus:outline-none`}
                onClick={() => changeTab(1)}
              >
                <div className="flex ">
                  <p className="mx-1">
                    <FaLink />{" "}
                  </p>
                  <p className="text-[12px]">
                    <b>Url / Link</b>{" "}
                  </p>
                </div>
              </button>
              <button
                className={`${
                  activeTab === 2
                    ? "bg-tealBlue text-white"
                    : "bg-lightBlue text-white-700"
                } py-2 px-4 rounded mx-2 sm:mx-4 border my-1 flex-col justify-center hover:bg-tealBlue  border-borderMuted  flex  focus:outline-none`}
                onClick={() => changeTab(2)}
              >
                <div className="flex ">
                  <p className="mx-1">
                    <IoCallOutline />{" "}
                  </p>
                  <p className="text-[12px]">
                    <b>Phone Call</b>{" "}
                  </p>
                </div>
              </button>
              <button
                className={`${
                  activeTab === 3
                    ? "bg-tealBlue text-white"
                    : "bg-lightBlue text-white-700"
                } py-2 px-4 rounded mx-2 sm:mx-4 border my-1 flex-col justify-center hover:bg-tealBlue  border-borderMuted  flex  focus:outline-none`}
                onClick={() => changeTab(3)}
              >
                <div className="flex ">
                  <p className="mx-1">
                    <TbGrave2 />{" "}
                  </p>
                  <p className="text-[12px]">
                    <b>Create Page</b>{" "}
                  </p>
                </div>
              </button>
              <button
                className={`${
                  activeTab === 4
                    ? "bg-tealBlue text-white"
                    : "bg-lightBlue text-white-700"
                } py-2 px-4 rounded mx-2 sm:mx-4 border my-1 flex-col justify-center hover:bg-tealBlue  border-borderMuted  flex  focus:outline-none`}
                onClick={() => changeTab(4)}
              >
                <div className="flex ">
                  <p className="mx-1">
                    <FaRegFilePdf />{" "}
                  </p>
                  <p className="text-[12px]">
                    <b>PDF</b>{" "}
                  </p>
                </div>
              </button>
              <button
                className={`${
                  activeTab === 5
                    ? "bg-tealBlue text-white"
                    : "bg-lightBlue text-white-700"
                } py-2 px-4 rounded mx-2 sm:mx-4 border my-1 flex-col justify-center hover:bg-tealBlue  border-borderMuted  flex  focus:outline-none`}
                onClick={() => changeTab(5)}
              >
                <div className="flex ">
                  <p className="mx-1">
                    <IoImage />
                  </p>
                  <p className="text-[12px]">
                    <b>Images/Videos</b>
                  </p>
                </div>
              </button>
              <button
                className={`${
                  activeTab === 6
                    ? "bg-tealBlue text-white"
                    : "bg-lightBlue text-white-700"
                } py-2 px-4 rounded mx-2 sm:mx-4 border my-1 flex-col justify-center hover:bg-tealBlue  border-borderMuted  flex  focus:outline-none`}
                onClick={() => changeTab(6)}
              >
                <div className="flex ">
                  <p className="mx-1">
                    <IoImage />
                  </p>
                  <p className="text-[12px]">
                    <b>Smart QR</b>
                  </p>
                </div>
              </button>
            </div>
            <div className="max-w-[800px] w-full mx-auto bg-white mt-4 sm:mt-6 rounded pt-3 pb-6 sm-pb-8 max-h-[800px] overflow-y-scroll">
              <div
                className={`tab-content p-4 rounded-md ${
                  activeTab === 3 ? " max-w-[750px]" : "max-w-[450px] "
                } w-full mx-auto`}
              >
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={`tab-pane relative`}>
                      {/* only for mobile */}
                      {window.innerWidth < 768 &&
                        tabMapper[activeTab] === "memories" && (
                          <div className="top-[0%] z-50 mb-4">
                            <button
                              type="submit"
                              disabled={disableButtonCondition()}
                              className="max-w-[350px] mx-auto flex items-center justify-center focus:outline-none  disabled:bg-gray-400 text-white text-[13px] sm:text-base font-bold sm:font-normal bg-[#0082B2] hover:bg-[#006d95] rounded py-3 w-full transition duration-150 ease-in whitespace-nowrap"
                            >
                              Save
                            </button>
                          </div>
                        )}

                      {tabMapper[activeTab] === "link" && (
                        <div className="flex flex-col mb-4 sm:mb-6">
                          <div className="relative">
                            <input
                              {...register("link")}
                              placeholder="Put your link here"
                              className={`w-full text-sm sm:text-base placeholder-gray-500 px-4 rounded-lg border ${
                                errors?.link
                                  ? "border-red-500"
                                  : "border-gray-400"
                              } w-full py-3 focus:outline-none focus:border-blue-400`}
                            />
                          </div>
                          {errors?.link && (
                            <span className="text-red-500 text-xs mt-1">
                              {errors.link.message}
                            </span>
                          )}
                        </div>
                      )}

                      {tabMapper[activeTab] === "phoneNumber" && (
                        <div className="flex flex-col mb-4 sm:mb-6">
                          <div className="relative">
                            <PhoneInputWithCountry
                              id="contactNum"
                              name="contactNum"
                              defaultCountry="US"
                              control={control}
                              className={`py-3 w-full text-sm sm:text-base placeholder-gray-500 px-4 rounded-lg border ${
                                errors?.contactNum
                                  ? "border-red-500"
                                  : "border-gray-400"
                              }`}
                            />
                          </div>
                          {errors?.contactNum && (
                            <span className="text-red-500 text-xs mt-1">
                              {errors.contactNum.message}
                            </span>
                          )}
                        </div>
                      )}

                      {tabMapper[activeTab] === "memories" && (
                        <div className="flex flex-col mb-4 sm:mb-6">
                          <div className="relative">
                            <Memories
                              data={barcodeData}
                              shouldShowLink={shouldShowLink}
                              link={link}
                              handleSubmit={handleSubmit}
                              onSubmit={onSubmit}
                            />
                          </div>
                          {errors?.contactNum && (
                            <span className="text-red-500 text-xs mt-1">
                              {errors.contactNum.message}
                            </span>
                          )}
                        </div>
                      )}

                      {tabMapper[activeTab] === "pdf" && (
                        <Pdf
                          data={barcodeData}
                          getBarcodeData={getBarcodeData}
                        />
                      )}

                      {tabMapper[activeTab] === "image" && (
                        <>
                          <ImageVideos data={barcodeData} />
                        </>
                      )}

                      {tabMapper[activeTab] === "smartQR" && (
                        <>
                          <SmartOR
                            errors={errors}
                            data={barcodeData}
                            activeTab={tabMapper[activeTab]}
                          />
                        </>
                      )}
                      {tabMapper[activeTab] !== "memories" &&
                        tabMapper[activeTab] !== "smartQR" && (
                          <div className="flex flex-col mb-4 sm:mb-6">
                            <div className="relative">
                              <input
                                {...register("name")}
                                className={`w-full text-sm sm:text-base placeholder-gray-500 px-4 rounded-lg border ${
                                  errors?.name
                                    ? "border-red-500"
                                    : "border-gray-400"
                                } w-full py-3 focus:outline-none focus:border-blue-400`}
                                placeholder="Name your QR (optional)"
                              />
                            </div>
                            {errors?.name && (
                              <span className="text-red-500 text-xs mt-1">
                                {errors.name.message}
                              </span>
                            )}
                          </div>
                        )}

                      <div className="sticky top-[0%] z-50 mb-4">
                        <button
                          type="submit"
                          disabled={disableButtonCondition()}
                          className="focus:outline-none  disabled:bg-gray-400 text-white text-[13px] sm:text-base font-bold sm:font-normal bg-[#0082B2] hover:bg-[#006d95] rounded-lg py-3 w-full transition duration-150 ease-in"
                        >
                          <span className="mr-2">Save</span>
                        </button>
                        <div className="">
                          <button
                            onClick={handleSubmit(async (data) => {
                              const previewWindow = window.open("", "_blank");
                              if (previewWindow) {
                                previewWindow.document.write(
                                  "Saving and preparing page preview..."
                                );
                              }

                              await onSubmit(data);
                              if (link && previewWindow) {
                                previewWindow.location.href = `https://${link}`;
                              } else if (previewWindow) {
                                previewWindow.close();
                              }
                            })}
                            type="button"
                            className="mt-4 inline-block w-full text-center bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue text-[13px] sm:text-base font-bold sm:font-normal"
                          >
                            Preview
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {processing ? (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"
              role="status"
            >
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default EditQr;
